import React, { useState } from 'react';
import { ShoppingBag, ShoppingCart, Store, Globe, Star, Clock, ExternalLink, Tag, Hash, Package, ImageOff } from 'lucide-react';
import { formatToAccountTime } from '../../utils/dateUtils';
import { getFormattedCurrency } from '../../utils/currencyUtils';
import { getWebsiteIcon } from '../../constants/statusConstants';
import { RESULTS_SECTION_STRINGS, COMMON } from '../../constants/appConstants';
import styles from './ProductCard.module.scss';

const WebsiteIconMap = { ShoppingBag, ShoppingCart, Store, Globe };

/** Color map for website badges */
const WEBSITE_COLORS = {
  amazon: { bg: '#FF9900', text: '#fff' },
  ebay: { bg: '#E53238', text: '#fff' },
  flipkart: { bg: '#2874F0', text: '#fff' },
  shopify: { bg: '#96BF48', text: '#fff' },
  snapdeal: { bg: '#E40046', text: '#fff' },
  noon: { bg: '#FEEE00', text: '#222' },
  sharafdg: { bg: '#004B93', text: '#fff' },
  ntsuae: { bg: '#1a1a2e', text: '#fff' },
  seazoneuae: { bg: '#0077B6', text: '#fff' },
  empiremarine: { bg: '#0D3B66', text: '#fff' },
  climaxmarine: { bg: '#1B4965', text: '#fff' },
  amitretail: { bg: '#6C63FF', text: '#fff' },
};

const ProductCard = React.memo(({ item, userTimezone }) => {
  const [imgError, setImgError] = useState(false);

  const formatPrice = (price, currency) => {
    if (!price || price === COMMON.NA) return COMMON.NA;
    return `${getFormattedCurrency(currency)}${price}`;
  };

  const websiteKey = (item.WEBSITE || '').toLowerCase().replace(/\s+/g, '');
  const websiteColor = WEBSITE_COLORS[websiteKey] || { bg: '#424242', text: '#fff' };
  const IconComponent = WebsiteIconMap[getWebsiteIcon(item.WEBSITE)] || Globe;

  const hasImage = item.IMAGE && item.IMAGE !== 'N/A' && item.IMAGE !== 'NA' && !imgError;
  const hasRating = item['SELLER RATING'] && item['SELLER RATING'] !== COMMON.NA;
  const price = formatPrice(item.PRICE, item.CURRENCY);
  const isNA = price === COMMON.NA;

  return (
    <div className={styles.card}>
      {/* Image Section */}
      <div className={styles.imageSection}>
        {hasImage ? (
          <img
            src={item.IMAGE}
            alt={item['PRODUCT NAME'] || item.PRODUCT}
            className={styles.productImage}
            onError={() => setImgError(true)}
            loading="lazy"
          />
        ) : (
          <div className={styles.imagePlaceholder}>
            <ImageOff size={32} strokeWidth={1.5} />
            <span>No image</span>
          </div>
        )}

        {/* Website badge overlay */}
        <div
          className={styles.websiteBadge}
          style={{ background: websiteColor.bg, color: websiteColor.text }}
        >
          <IconComponent size={12} />
          <span>{item.WEBSITE}</span>
        </div>

        {/* Rating overlay */}
        {hasRating && (
          <div className={styles.ratingBadge}>
            <Star size={11} fill="#FBBF24" stroke="#FBBF24" />
            <span>{item['SELLER RATING']}</span>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className={styles.content}>
        {/* Brand */}
        <div className={styles.brand}>
          {item.BRAND || RESULTS_SECTION_STRINGS.CARD.UNKNOWN_BRAND}
        </div>

        {/* Product Title */}
        <h4 className={styles.title}>
          {item['PRODUCT NAME'] || item.PRODUCT}
        </h4>

        {/* Meta tags row */}
        <div className={styles.metaTags}>
          {item.PRODUCT && (
            <span className={styles.metaTag}>
              <Package size={11} />
              {item.PRODUCT}
            </span>
          )}
          {item['OEM NUMBER'] && item['OEM NUMBER'] !== COMMON.NA && (
            <span className={styles.metaTag}>
              <Tag size={11} />
              {item['OEM NUMBER']}
            </span>
          )}
          {item['ASIN NUMBER'] && item['ASIN NUMBER'] !== COMMON.NA && (
            <span className={styles.metaTag}>
              <Hash size={11} />
              {item['ASIN NUMBER']}
            </span>
          )}
        </div>

        {/* Price + Footer */}
        <div className={styles.footer}>
          <div className={styles.priceRow}>
            <span className={`${styles.price} ${isNA ? styles.priceNA : ''}`}>
              {price}
            </span>
          </div>

          <div className={styles.actions}>
            <div className={styles.scrapedTime}>
              <Clock size={12} />
              <span>{formatToAccountTime(item['DATE SCRAPED'], userTimezone, 'short')}</span>
            </div>
            <a
              href={item['SOURCE URL']}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.viewBtn}
            >
              <span>{RESULTS_SECTION_STRINGS.CARD.VIEW_PRODUCT}</span>
              <ExternalLink size={13} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;
